import {Checkbox, Grid, IconButton, Theme, Typography, useMediaQuery, useTheme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import HelpIcon from '@material-ui/icons/Help';
import {
    BookingData,
    getAvailableRoomOptions,
    getAvailableTravellerOptions,
    GuestData,
    OptionValue,
    RoomData
} from '../helperFunctions';
import {Group, GroupResourceOption} from '../../../graphql/groups.datatypes';
import ContactUs from '../../../components/ContactUs';
import {useT} from 'react-polyglot-hooks';

import GuestOptionList, {GuestOptionListData} from './GuestOptionList';
import BookingToolDialog from '../BookingToolDialog';
import ExpandableSection from '../ExpandableSection';

const useStyles = makeStyles((theme: Theme) => ({
    heading: {
        marginTop: theme.spacing(4),
    },
    body: {
        fontSize: 16,

        '& li': {
            marginLeft: '1.2em',
        }
    },
    unitCost: {
        fontSize: 18,
        color: theme.palette.secondary.main,
        textAlign: 'right',
    },
    checkbox: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: -theme.spacing(1.5),

        '& .MuiCheckbox-root': {
            color: theme.palette.secondary.main,
        },

        '& .MuiCheckbox-root.Mui-disabled': {
            color: '#B2B2B2',
        },
    },
    sectionHeading: {
        marginTop: theme.spacing(2.5),
    },
    guestOption: {
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#B2B2B2',
        marginTop: theme.spacing(3.5),
        marginBottom: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
    },
    optionRightColumn: {
        [theme.breakpoints.up('sm')]: {
            textAlign: 'right',
        },
    },
    dialogLaunchButton: {
        color: theme.palette.secondary.main,
    },
    optionTitleText: {
        fontSize: 18,
    },
    optionDescription: {
        marginTop: 10,
        marginBottom: theme.spacing(1),
    },
    optionPriceText: {
        fontSize: 18,
        color: theme.palette.secondary.main,
    },
    optionRemainingText: {
        fontSize: 14,
    },
    helpButton: {
        color: '#B2B2B2',
    },
}));

interface Props {
    roomData: RoomData;
    bookingData: BookingData;
    group: Group;
    setRoomData: (roomData: RoomData) => void;
    setGroup: (group: Group) => void;
}

const RoomParty = (props: Props) => {
    const {roomData, bookingData, group, setRoomData, setGroup} = props;
    const classes = useStyles();
    const availableRoomOptions = getAvailableRoomOptions(group, bookingData, roomData);
    const availableTravellerOptions = getAvailableTravellerOptions(group, bookingData, roomData);
    const t = useT();

    const setGroupOptionRemaining = (optionId: number, diff: number) => {
        const groupResourceOption = group.options!.find(option => optionId === option.option.id);
        if (groupResourceOption && groupResourceOption.option && groupResourceOption.option.resource && groupResourceOption.option.resource.consumable) {
            const resource = groupResourceOption.option.resource;
            const options = [...group.options!.filter(option => option.id !== groupResourceOption.id), {
                ...groupResourceOption,
                option: {
                    ...groupResourceOption.option,
                    resource: {
                        ...resource,
                        resource_banks: [
                            {
                                ...resource.resource_banks![0],
                                remaining: resource.resource_banks![0].remaining! + diff,
                            }
                            , ...resource.resource_banks!.slice(1)]
                    }
                }
            }];
            setGroup({...group, options});
        }
    };

    const handleRoomOptionChange = (optionId: number, value: number) => {
        const optionValues = roomData.optionValues.map(optionValue => optionValue.optionId === optionId ? {
            ...optionValue,
            value
        } : optionValue);

        setRoomData({...roomData, optionValues});
        setGroupOptionRemaining(optionId, value > 0 ? -1 : 1);
    };

    const handleSelectAll = (optionId: number, value: number) => {
        let alreadySet = 0;

        roomData.guestsData.map(guestData => {
            guestData.optionValues.map(optionValue => {
                if (optionValue.optionId === optionId && optionValue.value > 0) alreadySet += 1;
            });
        });

        const guestsData = roomData.guestsData.map(guestData => {
            const optionValues = guestData.optionValues.map(optionValue => optionValue.optionId === optionId ? {
                ...optionValue,
                value
            } : optionValue);
            return {...guestData, optionValues};
        });

        setRoomData({...roomData, guestsData});
        setGroupOptionRemaining(optionId, value > 0 ? (guestsData.length - alreadySet) * -1 : guestsData.length);
    };

    const handleGuestOptionChange = (targetData: GuestData, optionId: number, value: number, diff: number) => {
        const guestsData = roomData.guestsData.map(guestData => {
            if (targetData.id !== guestData.id) return guestData;
            const optionValues = guestData.optionValues.map(optionValue => optionValue.optionId === optionId ? {
                ...optionValue,
                value
            } : optionValue);
            return {...guestData, optionValues};
        });

        setRoomData({...roomData, guestsData});
        setGroupOptionRemaining(optionId, diff * -1);
    };

    const isSelectAllActive = (guestOption: GroupResourceOption) => {
        return roomData.guestsData
            .filter(guestData => guestData.optionValues.find(optionValue => optionValue.optionId === guestOption.option.id!) === undefined)
            .length === 0;
    };

    const checkAllSelected = (guestOption: GroupResourceOption) => {
        return roomData.guestsData
            .map(guestData => {
                const checkValue = guestData.optionValues.find(optionValue => optionValue.optionId === guestOption.option.id!);
                return !!(checkValue && checkValue.value !== 0);
            })
            .indexOf(false) === -1;
    };

    const [openContactModal, setOpenContactModal] = React.useState({message: '', open: false});
    const handleClickOpen = (resourceName: string) => setOpenContactModal({
        message: `No resource ${resourceName} available in group ${group.id} `,
        open: true
    });
    const handleClose = () => setOpenContactModal({message: '', open: false});

    const [focusedOptionId, setFocusedOptionId] = useState<number | null>(null);
    const [guestOptionListData, setGuestOptionListData] = useState<GuestOptionListData | null>(null);

    useEffect(() => {
        if (focusedOptionId) {
            const guestOptionData = availableTravellerOptions.find(opt => {
                return opt.option.id === focusedOptionId
            });
            const newGuestOptionListData = guestOptionData ?
                getTravelerOptionData(
                    roomData,
                    guestOptionData,
                    checkAllSelected(guestOptionData)
                ) :
                null;
            setGuestOptionListData(newGuestOptionListData);
        } else {
            setGuestOptionListData(null);
        }
    }, [bookingData, focusedOptionId]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
        defaultMatches: false
    });

    return (
        <React.Fragment>
            {isMobile && guestOptionListData && (
                <BookingToolDialog
                    title={guestOptionListData.resource.display_name}
                    open={Boolean(guestOptionListData)}
                    onClose={() => setFocusedOptionId(null)}
                >
                    <GuestOptionList
                        {...guestOptionListData}
                        handleGuestOptionChange={handleGuestOptionChange}
                        handleSelectAll={handleSelectAll}
                    />
                </BookingToolDialog>
            )}
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="h3" className={classes.heading}>
                        Room {bookingData.roomsData.indexOf(roomData) + 1} Add-Ons:
                    </Typography>
                </Grid>
            </Grid>

            {availableRoomOptions
                .filter(roomOption => !roomOption.is_hidden)
                .map((roomOption, roomIndex) => {
                    const currentResource = roomOption.option.resource!;
                    const remaining = currentResource.consumable && !currentResource.allow_manual_overbooking ? currentResource.resource_banks![0].remaining! : 999999;
                    // const isAvailable = remaining > currentResource.resource_banks![0].warning_count!;
                    const isAvailable = remaining > 0;
                    const roomResource = roomData.optionValues.find(optionValue => optionValue.optionId === roomOption.option.id);
                    const isSelected = roomResource && roomResource.value > 0;

                    return roomResource && (
                        <Grid
                            container
                            key={roomIndex}
                            className={classes.guestOption}
                            justify="space-between"
                        >
                            <Grid item xs={12} sm={9}>
                                <Typography
                                    variant="h4"
                                    className={clsx(classes.body, classes.optionTitleText)}
                                >
                                    <strong>{currentResource.display_name}</strong>
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={3} className={classes.optionRightColumn}>
                                <Typography
                                    variant="body1"
                                    className={clsx(classes.body, classes.optionPriceText)}
                                >
                                    <strong>
                                        {currentResource.has_price ? `$${roomOption.unitCost}` : '$ variable'}
                                    </strong>
                                </Typography>

                            </Grid>

                            <Grid item xs={12} className={classes.optionDescription}>
                                <Typography
                                    variant="body1"
                                    className={classes.body}
                                    dangerouslySetInnerHTML={{__html: currentResource.description || ''}}
                                />
                            </Grid>

                            <Grid item xs={isAvailable ? 12 : 9}>
                                {!currentResource.allow_multiple && isAvailable && roomData.guestsData.length < remaining && (
                                    <Typography component="label" variant="body1"
                                                className={clsx(classes.body, classes.checkbox)}>
                                        <Checkbox
                                            onChange={(event) => handleRoomOptionChange(roomOption.option.id!, event.target.checked ? 1 : 0)}
                                            checked={isSelected}
                                        /> All Room Travelers
                                    </Typography>
                                )}
                            </Grid>

                            {!isAvailable && (
                                <Grid item xs={3} className={classes.optionRightColumn}>
                                    <IconButton
                                        className={classes.helpButton}
                                        aria-label="help"
                                        size="small"
                                        onClick={() => handleClickOpen(currentResource.internal_name)}
                                    >
                                        <HelpIcon fontSize="inherit"/>
                                    </IconButton>
                                </Grid>
                            )}
                        </Grid>
                    );
                }
            )}

            <Grid container spacing={1} className={classes.sectionHeading}>
                <Grid item xs={12}>
                    <Typography variant="h3">Traveler Add-Ons:</Typography>
                </Grid>
            </Grid>

            {availableTravellerOptions
                .filter(roomOption => !roomOption.is_hidden)
                .map((guestOption, optionIndex) => {
                    const {
                        remaining,
                        resource: currentResource,
                        isAvailable,
                        allSelected,
                        optionId,
                        guestsData
                    } = getTravelerOptionData(roomData, guestOption, checkAllSelected(guestOption))
                    const currentOption = (optionValue: OptionValue) => optionValue.optionId === optionId;
                    const guestDataFilter = (guestData: GuestData) => guestData.optionValues.find(currentOption);
                    const drawerOpen = !isMobile && focusedOptionId === optionId;

                    return guestsData.filter(guestDataFilter).length > 0 && (
                        <Grid
                            container
                            key={optionIndex}
                            className={classes.guestOption}
                            justify="space-between"
                        >
                            <Grid item xs={12} sm={10}>
                                <Typography
                                    variant="h4"
                                    className={clsx(classes.body, classes.optionTitleText)}
                                >
                                    <strong>{currentResource.display_name}</strong>
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={2} className={classes.optionRightColumn}>
                                <Typography
                                    variant="body1"
                                    className={clsx(classes.body, classes.optionPriceText)}
                                >
                                    <strong>
                                        {currentResource.has_price ? `$${guestOption.unitCost}` : '$ variable'}
                                    </strong>

                                </Typography>

                            </Grid>

                            <Grid item xs={12} className={classes.optionDescription}>
                                <Typography
                                    variant="body1"
                                    className={classes.body}
                                    dangerouslySetInnerHTML={{__html: currentResource.description || ''}}
                                />
                            </Grid>

                            <Grid item xs={10}>
                                {!currentResource.allow_multiple && isAvailable && roomData.guestsData.length < remaining && (
                                    <Typography component="label" variant="body1"
                                                className={clsx(classes.body, classes.checkbox)}>
                                        <Checkbox
                                            onChange={() => handleSelectAll(optionId, allSelected ? 0 : 1)}
                                            checked={allSelected}
                                            disabled={!isSelectAllActive(guestOption)}
                                        /> All Room Travelers
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={2} className={classes.optionRightColumn}>
                                {isAvailable && (
                                    <Grid item>
                                        <IconButton
                                            className={classes.dialogLaunchButton}
                                            onClick={() => {
                                                setFocusedOptionId(focusedOptionId === optionId ? null : optionId)
                                            }}
                                        >
                                            {drawerOpen ?
                                                <ExpandLess fontSize="inherit"/> :
                                                <ExpandMore fontSize="inherit"/>
                                            }
                                        </IconButton>
                                    </Grid>
                                )}
                                {!isAvailable && (
                                    <IconButton
                                        className={classes.helpButton}
                                        aria-label="help"
                                        size="small"
                                        onClick={() => handleClickOpen(currentResource.internal_name)}
                                    >
                                        <HelpIcon fontSize="inherit"/>
                                    </IconButton>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <ExpandableSection open={drawerOpen}>
                                    <GuestOptionList
                                        allSelected={allSelected}
                                        guestsData={guestsData}
                                        isAvailable={isAvailable}
                                        optionId={optionId}
                                        remaining={remaining}
                                        resource={currentResource}
                                        showAllCheckbox={false}
                                        numberGuests={false}
                                        handleGuestOptionChange={handleGuestOptionChange}
                                        handleSelectAll={handleSelectAll}
                                    />
                                </ExpandableSection>
                            </Grid>
                        </Grid>
                    )
                })
            }

            <ContactUs
                onClose={handleClose}
                open={openContactModal.open}
                groupId={group.id}
                heading={'Contact us'}
                message={String(t('no-resource'))}
                adminMessage={openContactModal.message}
            />

        </React.Fragment>
    );
};

function getTravelerOptionData(
    roomData: RoomData,
    guestOption: GroupResourceOption,
    allSelected: boolean
): GuestOptionListData {
    const currentResource = guestOption.option.resource!;
    const remaining = currentResource.consumable && !currentResource.allow_manual_overbooking ? currentResource.resource_banks![0].remaining! : 999999;
    // const resourceBank = currentResource.resource_banks![0];
    // const isAvailable = remaining > resourceBank.warning_count!;
    const isAvailable = remaining > 0;

    return {
        remaining,
        resource: currentResource,
        isAvailable,
        allSelected,
        optionId: guestOption.option!.id!,
        guestsData: roomData.guestsData
    };
}

export default RoomParty;
